import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Bar as HorizontalBar } from 'react-chartjs-2';
import 'chartjs-plugin-datalabels';
import {
  FormatAsDollars,
  FormatInThousands,
} from './../../../utils/Helpers/Format';
import { useIntl } from 'react-intl';
import UserPrefsContext from '../../../context/UserPrefs/UserPrefsContext';

const CHART_HEIGHT = 372;

const CostToOwnComparisonChart = ({
  carNames,
  carsCosts,
  displayEvCostBreakdownOption,
  displayEvCostBreakdown,
}) => {
  let chartData = {};
  const intl = useIntl();
  const userPrefs = useContext(UserPrefsContext);
  const showChargerCost = userPrefs.get('includeChargerCosts');

  chartData = {
    labels: carNames,
    datasets: [
      {
        label: intl.formatMessage({
          id: 'graph.costOfOwnership.chart.vehicle',
          defaultMessage: 'Vehicle payments',
        }),
        backgroundColor: `#${process.env.REACT_APP_COMPONENTS_COST_TO_OWN_COMPARISION_CHART_BAR_COLOR_NET}`,
        data: carsCosts.map((carCost) => {
          return carCost.vehicle.total;
        }),
        abbreviatedLabel: '',
      },
      {
        label: intl.formatMessage({
          id: 'graph.costOfOwnership.chart.taxesFees',
          defaultMessage: 'Taxes and Fees',
        }),
        backgroundColor: `#${process.env.REACT_APP_COMPONENTS_COST_TO_OWN_COMPARISION_CHART_BAR_COLOR_TAXESFEES}`,
        data: carsCosts.map((carCost) => {
          return carCost.taxesFees.total;
        }),
        abbreviatedLabel: '',
      },
      ...(showChargerCost
        ? [
            {
              label: intl.formatMessage({
                id: 'graph.costOfOwnership.chart.charger',
                defaultMessage: 'Charger/Install',
              }),
              backgroundColor: `#${process.env.REACT_APP_COMPONENTS_COST_TO_OWN_COMPARISION_CHART_BAR_COLOR_CHARGER}`,
              data: carsCosts.map((carCost) => {
                return carCost.charger.total;
              }),
              abbreviatedLabel: '',
            },
          ]
        : []),
      {
        label: intl.formatMessage({
          id: 'graph.costOfOwnership.chart.electricity',
          defaultMessage: 'Electricity',
        }),
        backgroundColor: `#${process.env.REACT_APP_COMPONENTS_COST_TO_OWN_COMPARISION_CHART_BAR_COLOR_ELECTRICITY}`,
        data: carsCosts.map((carCost) => {
          return carCost.electricity.total;
        }),
        abbreviatedLabel: '',
      },
      {
        label: intl.formatMessage({
          id: 'graph.costOfOwnership.chart.gasoline',
          defaultMessage: 'Gasoline',
        }),
        backgroundColor: `#${process.env.REACT_APP_COMPONENTS_COST_TO_OWN_COMPARISION_CHART_BAR_COLOR_GASOLINE}`,
        data: carsCosts.map((carCost) => {
          return carCost.gasoline.total;
        }),
        abbreviatedLabel: '',
      },
      {
        label: intl.formatMessage({
          id: 'graph.costOfOwnership.chart.maintenance',
          defaultMessage: 'Maintenance',
        }),
        backgroundColor: `#${process.env.REACT_APP_COMPONENTS_COST_TO_OWN_COMPARISION_CHART_BAR_COLOR_MAINTENANCE}`,
        borderColor: `#000000`,
        data: carsCosts.map((carCost) => {
          return carCost.maintenance.total;
        }),
        abbreviatedLabel: '',
      },
      {
        label: intl.formatMessage({
          id: 'graph.costOfOwnership.chart.insurance',
          defaultMessage: 'Insurance',
        }),
        backgroundColor: `#${process.env.REACT_APP_COMPONENTS_COST_TO_OWN_COMPARISION_CHART_BAR_COLOR_INSURANCE}`,
        data: carsCosts.map((carCost) => {
          return carCost.insurance.total;
        }),
        abbreviatedLabel: '',
      },
      {
        showLine: false,
        legend: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        datalabels: {
          align: 'right',
          color: '#142C41',
        },
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    indexAxis: 'y',
    animation: {
      duration: 0,
    },
    interaction: {
      mode: 'index',
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          callback: function (value) {
            return FormatInThousands(value);
          },
          beginAtZero: true,
        },
      },
      y: {
        stacked: true,
        ticks: {
          font: {
            weight: 400,
          },
          color: '#142C41',
        },
      },
    },
    barPercentage: 0.6,
    categoryPercentage: 0.6,
    layout: {
      padding: {
        right: 75,
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.parsed.x;
            const label = context.dataset.label || '';
            return ' ' + label + ': ' + FormatAsDollars(value);
          },
          title: function (tooltipItems) {
            let total = tooltipItems.reduce(function (acc, item) {
              return acc + item.parsed.x;
            }, 0);
            return tooltipItems[0].label + ': ' + FormatAsDollars(total);
          },
        },
        yAlign: function (tooltipItem) {
          return tooltipItem.dataIndex > 0 ? 'center' : 'bottom';
        },
        xAlign: 'center',
      },
      legend: {
        position: 'bottom',
        onClick: function (e) {
          e.stopPropagation();
        },
        display: true,
        labels: {
          usePointStyle: true,
          boxWidth: 12,
          filter: function (item) {
            return !!item?.text?.trim();
          },
          font: {
            size: 14,
            weight: 400,
          },
          color: '#142C41',
        },
      },
      datalabels: {
        formatter: (value, ctx) => {
          let datasets = ctx.chart.data.datasets;
          if (ctx.datasetIndex === datasets.length - 1) {
            let sum = 0;
            datasets.forEach((dataset) => {
              sum += dataset.data[ctx.dataIndex];
            });
            return FormatAsDollars(sum);
          } else if (value === 0) {
            return '';
          } else {
            return ctx.dataset.abbreviatedLabel;
          }
        },
        color: '#fff',
        font: {
          weight: 500,
        },
        anchor: 'center',
      },
    },
  };

  // Explicit parent height keeps the canvas sized correctly when this chart is
  // first rendered hidden (e.g. a tablet rotating from portrait to landscape).
  return (
    <div style={{ position: 'relative', height: CHART_HEIGHT }}>
      <HorizontalBar data={chartData} height={CHART_HEIGHT} options={options} />
    </div>
  );
};

export default CostToOwnComparisonChart;

CostToOwnComparisonChart.propTypes = {
  carNames: PropTypes.array,
  carCosts: PropTypes.array,
  displayEvCostBreakdownOption: PropTypes.bool,
  displayEvCostBreakdown: PropTypes.bool,
  displayFuelOnly: PropTypes.bool,
};
